<script setup lang="ts">
/**
 * 航线折线、地图航点、航点列表弹窗、航点照片（compare-pop）及交互
 */
import { onUnmounted, watch } from 'vue'
import { store } from '@/store'
import { getWayLineInfo } from '@/api/eventApi/index'
import { flyToCamera } from 'vue-cesium/es/utils/cesium-helpers'
import ComparePop from '../compare-pop/index.vue'

const props = defineProps<{
    /** Cesium Viewer 实例，与父级 readyViewer 同步 */
    viewer: any
}>()

const polylineActive = ref(false)
const pointlistActive = ref(false)
const wayLineObj = ref()
const wayLineList: any = ref([])
const wayLinePoint: any = ref([])

watch(
    () => store.informationListStore.informationStore().wayLineId,
    (data: any) => {
        store.informationListStore.informationStore().setTreatActive(false)
        if (data && props.viewer) {
            polylineActive.value = true
            pointlistActive.value = true
            getWayLineInfo(data).then((res) => {
                wayLineObj.value = res
                wayLineList.value = []
                res.point_list.forEach((result: any) => {
                    wayLineList.value.push({
                        lng: result.longitude_point,
                        lat: result.latitude_point,
                        height: result.point_height,
                        id: result.id,
                        sort_field: result.sort_field,
                        is_photo: result.is_photo,
                    })
                })

                wayLinePoint.value = res.point_list.filter((outerItem: any) => {
                    return outerItem.group_list.some((innerItem: any) =>
                        innerItem.action_list.some(
                            (item: any) => item.action_actuator_func === 'takePhoto' || 'orientedShoot'
                        )
                    )
                })

                const CesiumGlobal = (globalThis as any).Cesium
                if (wayLineObj.value.wayline_longth > 1000) {
                    flyToCamera(props.viewer, {
                        position: CesiumGlobal.Cartesian3.fromDegrees(
                            wayLineList.value[0].lng - 0.005,
                            wayLineList.value[0].lat - 0.032,
                            3000
                        ),
                        heading: 6.283185307179586,
                        pitch: -0.5001412827126326,
                        roll: 6.283185307179586,
                    })
                } else {
                    flyToCamera(props.viewer, {
                        position: CesiumGlobal.Cartesian3.fromDegrees(
                            wayLineList.value[0].lng + 0.0005,
                            wayLineList.value[0].lat - 0.010,
                            500
                        ),
                        heading: 6.283185307179586,
                        pitch: -0.5001412827126326,
                        roll: 6.283185307179586,
                    })
                }
            })
        } else {
            polylineActive.value = false
            pointlistActive.value = false
            wayPointVisible.value = false
            selectedWaypointListIndex.value = null
        }
    },
    { deep: true }
)

const closeWaypointList = () => {
    pointlistActive.value = false
}

const wayPointVisible = ref(false)
const wayPointObj = ref()
const selectedWaypointListIndex = ref<number | null>(null)

const openWaypointCompare = (point: any, listIndex?: number | string | null) => {
    const idx = listIndex == null ? null : Number(listIndex)
    if (idx !== null && wayPointVisible.value && selectedWaypointListIndex.value === idx) {
        wayPointVisible.value = false
        selectedWaypointListIndex.value = null
        return
    }
    wayPointObj.value = point
    wayPointVisible.value = true
    selectedWaypointListIndex.value = idx
}

const isSameWaypointPoint = (a: any, b: any) => {
    if (!a || !b) return false
    if (a.id != null && b.id != null && a.id !== '' && b.id !== '') {
        return String(a.id) === String(b.id)
    }
    const eps = 1e-6
    return (
        Math.abs(Number(a.longitude_point) - Number(b.longitude_point)) < eps &&
        Math.abs(Number(a.latitude_point) - Number(b.latitude_point)) < eps
    )
}

const waypointMarkerColor = (item: any) => {
    if (wayPointVisible.value && isSameWaypointPoint(wayPointObj.value, item)) {
        return '#FF4545'
    }
    const v = item?.is_photo
    const hasPhoto = v === true || v === 1 || v === '1'
    return hasPhoto ? '#00FFAE' : '#6CB8FF'
}

const isPickWaylineWaypoint = (picked: any) => {
    const CesiumGlobal = (globalThis as any).Cesium
    const viewer = props.viewer
    if (!CesiumGlobal?.defined?.(picked) || !picked?.id || !wayLinePoint.value?.length || !viewer) {
        return false
    }
    const ent = picked.id
    if (typeof ent?.position?.getValue !== 'function') return false
    try {
        const t = viewer.clock.currentTime
        const pos = ent.position.getValue(t)
        if (!pos) return false
        const cart = CesiumGlobal.Cartographic.fromCartesian(pos)
        const lng = CesiumGlobal.Math.toDegrees(cart.longitude)
        const lat = CesiumGlobal.Math.toDegrees(cart.latitude)
        const eps = 1e-5
        return wayLinePoint.value.some(
            (wp: any) =>
                Math.abs(Number(wp.longitude_point) - lng) < eps &&
                Math.abs(Number(wp.latitude_point) - lat) < eps
        )
    } catch {
        return false
    }
}

let globeWaypointPhotoClickCleanup: (() => void) | undefined

const attachGlobeClickCloseWaypointPhoto = () => {
    globeWaypointPhotoClickCleanup?.()
    globeWaypointPhotoClickCleanup = undefined
    const viewer = props.viewer
    const canvas = viewer?.scene?.canvas
    const CesiumGlobal = (globalThis as any).Cesium
    if (!canvas || !CesiumGlobal) return

    const onCanvasClick = (ev: MouseEvent) => {
        setTimeout(() => {
            if (!wayPointVisible.value) return
            const rect = canvas.getBoundingClientRect()
            const x = ev.clientX - rect.left
            const y = ev.clientY - rect.top
            const position = new CesiumGlobal.Cartesian2(x, y)
            const picked = viewer.scene.pick(position)
            if (isPickWaylineWaypoint(picked)) return
            wayPointVisible.value = false
            selectedWaypointListIndex.value = null
        }, 0)
    }

    canvas.addEventListener('click', onCanvasClick)
    globeWaypointPhotoClickCleanup = () => {
        canvas.removeEventListener('click', onCanvasClick)
    }
}

watch(
    () => props.viewer,
    (v) => {
        globeWaypointPhotoClickCleanup?.()
        globeWaypointPhotoClickCleanup = undefined
        if (v) attachGlobeClickCloseWaypointPhoto()
    },
    { immediate: true }
)

const waypointBtn = (list: any) => {
    if (wayPointVisible.value && isSameWaypointPoint(wayPointObj.value, list)) {
        wayPointVisible.value = false
        selectedWaypointListIndex.value = null
        return
    }
    openWaypointCompare(list, null)
}

watch(wayPointVisible, (v) => {
    if (!v) selectedWaypointListIndex.value = null
})

onUnmounted(() => {
    globeWaypointPhotoClickCleanup?.()
    globeWaypointPhotoClickCleanup = undefined
})
</script>

<template>
    <!-- 置于 vc-viewer 内：航线与航点 -->
    <template v-if="polylineActive">
        <vc-entity>
            <vc-graphics-polyline :positions="wayLineList" material="yellow" :width="3" :clampToGround="false" />
        </vc-entity>
    </template>
    <template v-if="polylineActive">
        <template v-for="(item, wpIdx) in wayLinePoint" :key="item.id ?? `${wpIdx}-${item.longitude_point}`">
            <vc-entity :position="[item.longitude_point, item.latitude_point, item.point_height]" @click="waypointBtn(item)">
                <vc-graphics-point :color="waypointMarkerColor(item)" :pixel-size="5" />
            </vc-entity>
        </template>
    </template>

    <!-- 列表与照片弹窗挂到 body，避免受 viewer 内部布局影响 -->
    <Teleport to="body">
        <div class="waypoint-popup" v-if="pointlistActive">
            <div class="waypoint-popup-header">
                <span>航点列表</span>
                <span class="close-btn" @click="closeWaypointList">×</span>
            </div>
            <div class="waypoint-popup-content">
                <div class="waypoint-item" v-for="(item, index) in wayLinePoint" :key="index"
                    :class="{ 'is-active': selectedWaypointListIndex === index }"
                    @click="openWaypointCompare(item, index)">
                    <span class="waypoint-index">{{ index }}号点位</span>
                    <span class="waypoint-coord">
                        <span class="coord-label">经度:</span>
                        <span class="coord-value">{{ item.longitude_point || '--' }}</span>
                    </span>
                    <span class="waypoint-coord">
                        <span class="coord-label">纬度:</span>
                        <span class="coord-value">{{ item.latitude_point || '--' }}</span>
                    </span>
                </div>
            </div>
        </div>
        <compare-pop v-model:wayPointVisible="wayPointVisible" :wayPointObj="wayPointObj" :wayLineObj="wayLineObj" />
    </Teleport>
</template>

<style lang="scss" scoped>
.waypoint-popup {
    width: 235px;
    max-height: 400px;
    border-radius: 8px;
    opacity: 1;
    background: rgba(1, 33, 50, 0.95);
    position: fixed;
    top: 40%;
    left: 60%;
    transform: translate(-50%, -50%);
    z-index: 100;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.5);
    border: 1px solid rgba(11, 232, 246, 0.3);

    .waypoint-popup-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 12px 16px;
        background: linear-gradient(270deg, rgba(0, 133, 255, 0.1) 0%, rgba(0, 125, 255, 0.3) 100%);
        border-bottom: 1px solid rgba(11, 232, 246, 0.2);
        border-radius: 8px 8px 0 0;

        span:nth-child(1) {
            font-family: YouSheBiaoTiHei;
            font-size: 18px;
            color: #ffffff;
        }

        .close-btn {
            width: 24px;
            height: 24px;
            line-height: 24px;
            text-align: center;
            cursor: pointer;
            color: #0be8f6;
            font-size: 20px;
            transition: all 0.3s;

            &:hover {
                color: #ff6b6b;
                transform: scale(1.1);
            }
        }
    }

    .waypoint-popup-content {
        max-height: 340px;
        overflow-y: auto;
        padding: 8px;

        &::-webkit-scrollbar {
            width: 6px;
        }

        &::-webkit-scrollbar-track {
            background: rgba(0, 0, 0, 0.2);
            border-radius: 3px;
        }

        &::-webkit-scrollbar-thumb {
            background: rgba(11, 232, 246, 0.5);
            border-radius: 3px;

            &:hover {
                background: rgba(11, 232, 246, 0.8);
            }
        }
    }

    .waypoint-item {
        display: flex;
        flex-direction: column;
        padding: 10px 12px;
        margin-bottom: 8px;
        background: linear-gradient(270deg, rgba(73, 72, 79, 0.3) 0%, rgba(89, 83, 132, 0.3) 100%);
        border: 1px solid rgba(136, 141, 141, 0.3);
        border-radius: 6px;
        cursor: pointer;
        transition: all 0.3s ease;

        &:hover {
            background: linear-gradient(270deg, rgba(0, 133, 255, 0.3) 0%, rgba(0, 125, 255, 0.5) 100%);
            border-color: rgba(11, 232, 246, 0.5);
            transform: translateX(5px);
        }

        &:last-child {
            margin-bottom: 0;
        }

        &.is-active {
            background: linear-gradient(270deg, rgba(0, 133, 255, 0.45) 0%, rgba(0, 125, 255, 0.55) 100%);
            border-color: rgba(11, 232, 246, 0.85);
            box-shadow: 0 0 12px rgba(11, 232, 246, 0.25);
        }

        .waypoint-index {
            font-family: YouSheBiaoTiHei;
            font-size: 16px;
            color: #0be8f6;
            margin-bottom: 6px;
        }

        .waypoint-coord {
            display: flex;
            align-items: center;
            font-size: 12px;
            color: #b0b0b0;
            margin-bottom: 4px;
            font-family: AlibabaPuHuiTi;

            .coord-label {
                color: #888;
                margin-right: 4px;
            }

            .coord-value {
                color: #ffffff;
                font-family: monospace;
            }
        }
    }
}
</style>
